import { Drawer, Menu, Select } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { takeWhile } from 'lodash';
import { memo, useContext, useEffect, useMemo, useCallback, useState } from 'react';
import generateBreadcrumbs from './generateBreadcrumbs';
import ProgramsMenu from './ProgramsMenu';
import ProgramMenu from './ProgramMenu';
import StatesMenu from './StatesMenu';
import StateMenu from './StateMenu';
import DistrictMenu from './DistrictMenu';
import SchoolMenu from './SchoolMenu';
import ClassMenu from './ClassMenu';
import CustomMenu from './CustomMenu';
import ReportsSearch from '../../../components/navigation/ReportsSearch';
import { useQueryStringArgs, useReportsAccess, useUiData } from '../../../lib/hooks';
import UiContext from '../../../lib/UiContext';
import styles from './index.module.scss';
const uiKeys = ['semesters'];
const SideNavigation = ({
  isOpen,
  setIsOpen
}) => {
  const menuConfig = useReportsAccess().menu;
  const uiContext = useContext(UiContext);
  const queryStringArgs = useQueryStringArgs()[0];
  const {
    isPending,
    isError,
    data: uiData
  } = useUiData(uiKeys);
  const semesters = useMemo(() => isPending || isError ? [] : uiData.semesters?.filter(s => s.classCount > 0).sort((a, b) => a.order - b.order), [isPending, isError, uiData.semesters]);
  const [semester, setSemester] = useState();
  const [breadcrumbs, setBreadcrumbs] = useState();
  const updateBreadcrumbs = useCallback(e => setBreadcrumbs(arr => arr.slice(0, e.item.props.index + 1)), [setBreadcrumbs]);
  const addBreadcrumb = useCallback(e => {
    setBreadcrumbs(arr => [...arr, e]);
  }, [setBreadcrumbs]);
  const menu = useMemo(() => {
    const location = breadcrumbs?.at(-1);
    if (!location) {
      return;
    }
    const props = {
      semester: semester,
      setIsOpen: setIsOpen,
      updateState: addBreadcrumb
    };
    switch (location.type) {
      case 'Programs':
        return <ProgramsMenu {...props} />;
      case 'Program':
        return <ProgramMenu {...props} programId={location.id} />;
      case 'States':
        return <StatesMenu {...props} />;
      case 'State':
        return <StateMenu {...props} stateId={location.id} />;
      case 'District':
        return <DistrictMenu {...props} districtId={location.id} />;
      case 'School':
        return <SchoolMenu {...props} schoolId={location.id} />;
      case 'Class':
        return <ClassMenu {...props} classId={location.id} />;
      default:
        return <CustomMenu {...props} location={location} />;
    }
  }, [breadcrumbs, addBreadcrumb, semester, setIsOpen]);
  useEffect(() => {
    if (menuConfig && semesters && !semester) {
      const names = semesters.filter(e => Date.parse(e.earliestDate) <= Date.now()).map(e => e.name);
      const name = names.includes(uiContext.entity?.semester) ? uiContext.entity?.semester : semesters.filter(s => menuConfig.semesters?.includes(s.name))[0]?.name ?? names[0];
      setSemester(name);
    }
  }, [menuConfig, semesters, semester, uiContext.entity?.semester]);
  useEffect(() => {
    setIsOpen(menuConfig ? !!isOpen : null);
    setBreadcrumbs(generateBreadcrumbs(menuConfig, uiContext, queryStringArgs));
  }, [uiContext, menuConfig, isOpen, setIsOpen]);
  useEffect(() => {
    if (semester && breadcrumbs) {
      const newBreadcrumbs = takeWhile(breadcrumbs, e => !(e.semesters && !e.semesters.includes(semester)));
      if (newBreadcrumbs.length !== breadcrumbs.length) {
        setBreadcrumbs(newBreadcrumbs);
      }
    }
  }, [semester, breadcrumbs]);
  const closeSideNav = useCallback(() => setIsOpen(false), [setIsOpen]);
  return <Drawer placement="left" closable={false} bodyStyle={{
    padding: 0
  }} headerStyle={{
    padding: 0
  }} onClose={closeSideNav} open={isOpen} width={360} className={styles.drawer} data-sentry-element="Drawer" data-sentry-component="SideNavigation" data-sentry-source-file="index.jsx">
      <div className={styles["search-container"]} children={<ReportsSearch onSearch={closeSideNav} />} />
      <div className={styles["nav-ancestry"]}>
        <div className={styles["semester-selector"]}>
          <Select value={semester} onSelect={setSemester} children={semesters.map(({
          name
        }) => <Select.Option key={name} value={name} children={name} />)} data-sentry-element="Select" data-sentry-source-file="index.jsx" />
        </div>
        <Menu className={styles.breadcrumb} selectable={false} onClick={updateBreadcrumbs} items={breadcrumbs?.map((item, index, arr) => ({
        key: `${item.type}-${item.id}`,
        index: index,
        className: styles[arr.length - 1 === index ? 'last-breadcrumb-link' : 'breadcrumb-link'],
        disabled: arr.length - 1 === index,
        label: <div style={{
          marginLeft: index * 8
        }}>
                {item.icon ?? <FolderOpenOutlined />} {item.name}
              </div>
      }))} data-sentry-element="Menu" data-sentry-source-file="index.jsx" />
      </div>
      {menu}
    </Drawer>;
};
export default memo(SideNavigation);